import React, { useCallback, useMemo, useState } from "react";
import ProFeaturesModal from "../components/uiComponents/ProFeaturesModal";
import type { BillingPlanId, PlanTier } from "../types/billing";
import useLocalizedPricing from "./use-localized-pricing";

interface UseProUpgradeModalOptions {
  onUpgradeCheckout: (planId?: BillingPlanId) => Promise<boolean>;
  isUpgradeDisabled?: boolean;
  isUpgradeLoading?: boolean;
  currentPlanTier?: PlanTier;
  pricesConfigured?: Record<BillingPlanId, boolean>;
}

const useProUpgradeModal = ({
  onUpgradeCheckout,
  isUpgradeDisabled = false,
  isUpgradeLoading = false,
  currentPlanTier = "free",
  pricesConfigured,
}: UseProUpgradeModalOptions) => {
  const [isOpen, setIsOpen] = useState(false);
  const { proMonthly, agencyMonthly } = useLocalizedPricing();

  const openUpgradeModal = useCallback(() => {
    if (isUpgradeDisabled) {
      return;
    }
    setIsOpen(true);
  }, [isUpgradeDisabled]);

  const closeUpgradeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleUpgradeCheckout = useCallback(async (planId: BillingPlanId) => {
    const opened = await onUpgradeCheckout(planId);
    if (opened) {
      setIsOpen(false);
    }
    return opened;
  }, [onUpgradeCheckout]);

  const upgradeModal = useMemo(
    () => (
      <ProFeaturesModal
        isOpen={isOpen}
        onClose={closeUpgradeModal}
        onUpgrade={handleUpgradeCheckout}
        disabled={isUpgradeDisabled}
        loading={isUpgradeLoading}
        currentPlanTier={currentPlanTier}
        pricesConfigured={pricesConfigured}
        proPriceLabel={proMonthly}
        agencyPriceLabel={agencyMonthly}
      />
    ),
    [
      closeUpgradeModal,
      handleUpgradeCheckout,
      isOpen,
      isUpgradeDisabled,
      isUpgradeLoading,
      agencyMonthly,
      currentPlanTier,
      pricesConfigured,
      proMonthly,
    ]
  );

  return {
    openUpgradeModal,
    closeUpgradeModal,
    upgradeModal,
  };
};

export default useProUpgradeModal;
